<script setup lang="ts">
  import { ref, watch, onBeforeUnmount } from 'vue';
  import { ActionButtons } from '@/shared/ui';
  import { BranchFormSections, type BranchThemeColors } from '@/pages/admin/ui';
  import { useImageUploadStore } from '@/features/Document';
  import { useBranchManagement } from '@/pages/admin';
  import { useCustomToast } from '@/shared/lib';

  const { navigateToList } = useBranchManagement();
  const toast = useCustomToast();

  const filialName = ref('');
  const isFormValid = ref(false);

  // Theme colors
  const primaryColor = ref('#F8F4F4');
  const secondaryColor = ref('#854149');
  const textColor = ref('#2F2F2F');
  const primaryColorDark = ref('#1E1E1E');
  const secondaryColorDark = ref('#854149');
  const textColorDark = ref('#FFFFFF');
  const areColorsSelected = ref(true);

  const {
    hasImages,
    allUploadsComplete,
    handleImageUpload,
    handleImageProgress,
    handleImageComplete,
    clearState,
    createBranch: createBranchWithImage,
    loadingRequest,
  } = useImageUploadStore;

  const showDuplicateNameErrorToast = () => {
    toast.showErrorToast(
      {
        title: 'Este nome de Filial já existe',
        message: '',
        type: 'error',
      },
    );
  };

  const handleAllFilesDeleted = () => {
    hasImages.value = false;
    allUploadsComplete.value = false;
  };

  const handleColorsChanged = (colors: BranchThemeColors) => {
    primaryColor.value = colors.primaryColor;
    secondaryColor.value = colors.secondaryColor;
    textColor.value = colors.textColor;
    primaryColorDark.value = colors.primaryColorDark;
    secondaryColorDark.value = colors.secondaryColorDark;
    textColorDark.value = colors.textColorDark;
    areColorsSelected.value = true;
    validateForm();
  };

  const validateForm = () => {
    isFormValid.value = !!filialName.value.trim() && hasImages.value && areColorsSelected.value;
  };

  watch([filialName, hasImages], () => {
    validateForm();
  });

  const handleFormSave = async () => {
    if (!isFormValid.value) return;

    try {
      await createBranchWithImage({
        name: filialName.value,
        colorPrimary: primaryColor.value,
        colorSecondary: secondaryColor.value,
        colorText: textColor.value,
        colorPrimaryDark: primaryColorDark.value,
        colorSecondaryDark: secondaryColorDark.value,
        colorTextDark: textColorDark.value,
      });

      if (loadingRequest.value === 'success') {
        toast.showSuccessToast({
          title: 'Filial criada com sucesso',
          message: '',
          type: 'success',
        });
        clearState();
        navigateToList();
      }
    } catch (error: any) {
      console.error('Error creating branch:', error);

      const responseData = error?.response?.data;
      const errors = responseData?.errors;

      if (errors && typeof errors === 'object') {
        const hasDuplicateNameError = Object.values(errors).some(
          (errorArray: any) =>
            Array.isArray(errorArray) &&
            errorArray.some(
              (errorMsg: string) =>
                errorMsg.includes('Este nome de Filial já existe') ||
                errorMsg.includes('nome de Filial já existe') ||
                errorMsg.includes('já existe')
            )
        );

        if (hasDuplicateNameError) {
          showDuplicateNameErrorToast();
          return;
        }
      }

      toast.showErrorToast(
        {
          title: 'Erro ao criar filial. Por favor, tente novamente.',
          message: '',
          type: 'error',
        },
      );
    }
  };

  onBeforeUnmount(() => {
    clearState();
  });
</script>

<template>
  <section class="pt-4 p-6 text-gray-07">
    <div class="mt-4 mb-6 text-xl font-semibold flex justify-start">
      <span>Criar Filial</span>
    </div>

    <div
      class="w-full border border-border-color divide-y divide-gray-02 rounded-2xl text-sm bg-white-01"
    >
      <div class="p-6">
        <div class="h-auto flex flex-col gap-2">
          <div class="font-semibold flex">
            Nome da Filial
          </div>
          <input
            v-model="filialName"
            class="rounded-xl border flex items-center p-4 border-border-color bg-white-01"
            placeholder="Escreva o nome que representa a filial"
          >
        </div>
      </div>

      <BranchFormSections
        :primary-color="primaryColor"
        :secondary-color="secondaryColor"
        :text-color="textColor"
        :primary-color-dark="primaryColorDark"
        :secondary-color-dark="secondaryColorDark"
        :text-color-dark="textColorDark"
        :all-uploads-complete="allUploadsComplete"
        @files-selected="handleImageUpload"
        @upload-progress="handleImageProgress"
        @upload-complete="(_, imageIds) => handleImageComplete(imageIds)"
        @all-files-deleted="handleAllFilesDeleted"
        @colors-changed="handleColorsChanged"
      />
    </div>
    <ActionButtons
      :disabled="!isFormValid"
      :loading="loadingRequest === 'loading'"
      cancel-route="/admin/filiais"
      @save="handleFormSave"
    />
  </section>
</template>
